import React, { useState, useEffect } from "react";
import axios from "../config/axios";
import PageWrapper from "../components/common/PageWrapper";
import Header from "../components/common/Header";
import BodyWrapper from "../components/common/BodyWrapper";
import Footer from "../components/common/Footer";
import ContactInfo from "../components/contactPartials/ContactInfo";
import ContactForm from "../components/contactPartials/ContactForm";
import { useGeneral } from "../components/contexts/GeneralContext";
import FullScreenLoading from "../components/common/loading/FullScreenLoading";
import ApiErrorMessage from "../components/common/ApiErrorMessage";
import NoItemsFound from "../components/common/NoItemsFound";

const usePageContent = () => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [error, setError] = useState("");
  const { discountCoupons, setExtraContent } = useGeneral();

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const { data } = await axios.get("page-content/contact-us");
        if (active) setContent(data || null);
        setExtraContent((prev) => ({
          ...prev,
          socialChannels: data?.socialChannels,
           whatsAppNumber: data?.whatsAppNumber,
        }));
      } catch (err) {
        console.error(err);
        if (active)
          setError("Failed to load contact content. Please try again later.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  return { content, loading, error, discountCoupons };
};

const useScrollToTop = () => {
  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, []);
};

const ContactPage = () => {
  const { content, loading, error, discountCoupons } = usePageContent();
  useScrollToTop();

  return (
    <>
      {loading && <FullScreenLoading />}
      <PageWrapper loading={false} className="bg-bg min-h-screen">
        <Header code={discountCoupons} />

        <BodyWrapper loading={loading}>
          <div
            className={
              !error && content
                ? "grid grid-cols-1 xl:grid-cols-[4fr_6fr] gap-4 lg:gap-12  rounded-[15px] overflow-hidden items-start"
                : "grid grid-cols-1  gap-10 lg:gap-20  rounded-[15px] overflow-hidden p-10 2xl:p-20 2xl:py-12"
            }
          >
            {/* Left section - Conditional Info/Error/Empty */}
            {!error && content && (
              <div>
                {error ? (
                  <ApiErrorMessage
                    message1="Failed to load contact us content"
                    message2="Please check your connection and try again."
                  />
                ) : !content ? (
                  <NoItemsFound
                    message1="No content available"
                    message2="Please check back later."
                  />
                ) : (
                  <ContactInfo item={content} />
                )}
              </div>
            )}

            {/* Right section - Contact form always visible */}
            <div className="flex items-center justify-center w-full px-2 md:px-0">
              <ContactForm />
            </div>
          </div>
        </BodyWrapper>

        <Footer />
      </PageWrapper>
    </>
  );
};

export default ContactPage;
